/** @jsxImportSource @emotion/react */
import { EuiResizableContainer } from "@inscopix/ideas-eui";
import { DataTable, DataTableRowData } from "./DataTable";
import { DataTableFlyoutRight } from "./DataTableFlyoutRight";
import { useResizablePanels } from "components/Project/useResizablePanels";
import { useDataTableContext } from "../store/DataTableProvider";
import { DataTableFlyoutBottom } from "./DataTableFlyoutBottom";
import { css } from "@emotion/react";
import { GridApi } from "ag-grid-community";
import { AgGridReactProps } from "ag-grid-react";
import assert from "assert";

export interface DataTableContentProps {
  gridApi: GridApi<DataTableRowData> | undefined;
  setGridApi: (api: GridApi<DataTableRowData>) => void;
  handleSelectionChanged: AgGridReactProps["onSelectionChanged"];
  tableKey: string;
}
export const DataTableContent = ({
  gridApi,
  setGridApi,
  handleSelectionChanged,
  tableKey,
}: DataTableContentProps) => {
  const setSelectedFileId = useDataTableContext((s) => s.setSelectedFileId);
  const selectedFileId = useDataTableContext((s) => s.selectedFileId);
  const selectedTableId = useDataTableContext((s) =>
    s.tables.find((table) => table.key === tableKey),
  )?.id;
  assert(selectedTableId !== undefined);
  const flyoutRightContent = useDataTableContext((s) => s.flyoutRightContent);
  const setFlyoutRightContent = useDataTableContext(
    (s) => s.setFlyoutRightContent,
  );
  const flyoutBottomContent = useDataTableContext((s) => s.flyoutBottomContent);
  const setFlyoutBottomContent = useDataTableContext(
    (s) => s.setFlyoutBottomContent,
  );
  const isRightFlyoutOpen =
    flyoutRightContent === "tableNotes" || selectedFileId !== undefined;
  const { panels, handlePanelWidthChange } = useResizablePanels({
    isBottomFlyoutOpen: flyoutBottomContent !== undefined,
    isRightFlyoutOpen,
  });

  return (
    <EuiResizableContainer
      direction="horizontal"
      onPanelWidthChange={handlePanelWidthChange}
      style={{ width: "100%", height: "100%" }}
    >
      {(EuiResizablePanel, EuiResizableButton) => {
        const children = [
          <EuiResizablePanel
            key={panels.MAIN_AND_FLYOUT_BOTTOM.id}
            id={panels.MAIN_AND_FLYOUT_BOTTOM.id}
            panelRef={panels.MAIN_AND_FLYOUT_BOTTOM.ref}
            size={panels.MAIN_AND_FLYOUT_BOTTOM.size}
            paddingSize="none"
            // this main panel should not scroll, individual panels should
            style={{ overflowBlock: "visible", overflow: "visible" }}
          >
            <EuiResizableContainer
              direction="vertical"
              onPanelWidthChange={handlePanelWidthChange}
              style={{ width: "100%", height: "100%" }}
            >
              {(EuiResizablePanel, EuiResizableButton) => {
                const children = [
                  <EuiResizablePanel
                    key={panels.MAIN.id}
                    id={panels.MAIN.id}
                    panelRef={panels.MAIN.ref}
                    size={panels.MAIN.size}
                    paddingSize="none"
                  >
                    <DataTable
                      selectedTableId={selectedTableId}
                      handleSelectionChanged={handleSelectionChanged}
                      setGridApi={setGridApi}
                      gridApi={gridApi}
                    />
                  </EuiResizablePanel>,
                ];

                if (flyoutBottomContent !== undefined) {
                  children.push(
                    <EuiResizableButton
                      key="inner-resizer"
                      indicator="border"
                    />,
                    <EuiResizablePanel
                      key={panels.FLYOUT_BOTTOM.id}
                      id={panels.FLYOUT_BOTTOM.id}
                      panelRef={panels.FLYOUT_BOTTOM.ref}
                      size={panels.FLYOUT_BOTTOM.size}
                      paddingSize="none"
                      minSize="300px"
                      // prevent overscroll when flyout is open
                      css={css`
                        overflow-y: hidden;
                      `}
                    >
                      <DataTableFlyoutBottom
                        content={flyoutBottomContent}
                        onClose={() => setFlyoutBottomContent(undefined)}
                      />
                    </EuiResizablePanel>,
                  );
                }

                return <>{children}</>;
              }}
            </EuiResizableContainer>
          </EuiResizablePanel>,
        ];

        if (isRightFlyoutOpen) {
          children.push(
            <EuiResizableButton key="outer-resizer" indicator="border" />,
            <EuiResizablePanel
              key={panels.FLYOUT_RIGHT.id}
              id={panels.FLYOUT_RIGHT.id}
              panelRef={panels.FLYOUT_RIGHT.ref}
              size={panels.FLYOUT_RIGHT.size}
              paddingSize="none"
              minSize="300px"
            >
              <DataTableFlyoutRight
                tableId={selectedTableId}
                content={flyoutRightContent}
                fileId={selectedFileId}
                onClose={() =>
                  flyoutRightContent === "fileInfo"
                    ? setSelectedFileId(undefined)
                    : setFlyoutRightContent(undefined)
                }
              />
            </EuiResizablePanel>,
          );
        }

        return <>{children}</>;
      }}
    </EuiResizableContainer>
  );
};
